import {
  Utensils,
  Calendar,
  Shield,
  Users,
  Building,
  type LucideIcon,
} from "lucide-react";

export type AuthType = "oauth_code" | "oauth_client_credentials" | "api_key";

export interface IntegrationProvider {
  id: string;
  name: string;
  navText: string;
  description: string;
  icon: LucideIcon | string;
  iconType: "lucide" | "image";
  iconLight?: string;
  iconDark?: string;
  category: string;
  isPopular?: boolean;
  authType: AuthType;
  testEndpoint?: string; // Endpoint for connection validation
  formFields: {
    label: string;
    key: string;
    type: "text" | "password" | "email";
    placeholder: string;
    required: boolean;
  }[];
}

// DRY Helper functions for authentication
export const isOAuthType = (authType: AuthType): boolean =>
  authType.startsWith("oauth");

export const requiresUserAuth = (authType: AuthType): boolean =>
  authType === "oauth_code";

// Helper function to normalize hostname (remove protocol)
export const normalizeHostname = (hostname: string): string => {
  if (!hostname) return "";

  let normalized = hostname.trim();

  // Remove protocol if present
  if (normalized.startsWith("https://")) {
    normalized = normalized.substring(8);
  } else if (normalized.startsWith("http://")) {
    normalized = normalized.substring(7);
  }

  // Remove trailing slash if present
  if (normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
};

// UUID validation regex (v4 format)
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Helper function to extract and validate UUID from input
export const extractAndValidateUUID = (
  input: string,
): { uuid: string | null; isValid: boolean; error?: string } => {
  if (!input) {
    return {
      uuid: null,
      isValid: false,
      error: "Location External ID is required",
    };
  }

  const trimmed = input.trim();

  // Split by spaces and take the last part (in case user copied more text)
  const parts = trimmed.split(/\s+/);
  const lastPart = parts[parts.length - 1];

  // Test if it's a valid UUID
  if (UUID_REGEX.test(lastPart)) {
    return { uuid: lastPart, isValid: true };
  }

  return {
    uuid: lastPart,
    isValid: false,
    error:
      "Location External ID must be a valid UUID format (e.g., 12345678-1234-1234-1234-123456789abc)",
  };
};

export interface AuthConfigResult {
  config: Record<string, string>;
  isValid: boolean;
  errors: Record<string, string>;
}

export const getAuthConfig = (
  authType: AuthType,
  formData: FormData,
  providerId?: string,
): AuthConfigResult => {
  const errors: Record<string, string> = {};

  if (isOAuthType(authType)) {
    // Normalize hostname
    const input_hostname = formData.get("api_hostname") as string;
    const api_hostname = normalizeHostname(input_hostname);

    if (!api_hostname) {
      errors.api_hostname = "API hostname is required";
    }

    // Base OAuth config for both code and client_credentials
    const config = {
      api_hostname: api_hostname,
      client_id: formData.get("client_id") as string,
      client_secret: formData.get("client_secret") as string,
    };

    // Validate required fields
    if (!config.client_id) {
      errors.client_id = "Client ID is required";
    }
    if (!config.client_secret) {
      errors.client_secret = "Client Secret is required";
    }

    // Add Toast-specific config and validation
    if (providerId === "toast") {
      const input_id = formData.get("location_external_id") as string;
      const uuidResult = extractAndValidateUUID(input_id);

      if (!uuidResult.isValid) {
        errors.location_external_id =
          uuidResult.error || "Invalid Location External ID";
      }

      return {
        config: {
          ...config,
          location_external_id: uuidResult.uuid || input_id,
        },
        isValid: Object.keys(errors).length === 0,
        errors,
      };
    }

    return {
      config,
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  // API key config
  if (providerId === "mindbody") {
    const site_id = formData.get("site_id") as string;

    if (!site_id) {
      errors.site_id = "Site ID is required";
    }

    return {
      config: { site_id },
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  return {
    config: {},
    isValid: true,
    errors: {},
  };
};

// Shared OAuth form fields
const OAUTH_FORM_FIELDS = [
  {
    label: "API Hostname",
    key: "api_hostname",
    type: "text" as const,
    placeholder: "e.g., api.example.com",
    required: true,
  },
  {
    label: "Client ID",
    key: "client_id",
    type: "text" as const,
    placeholder: "Enter your Client ID",
    required: true,
  },
  {
    label: "Client Secret",
    key: "client_secret",
    type: "password" as const,
    placeholder: "Enter your Client Secret",
    required: true,
  },
];

// Toast-specific OAuth form fields (includes location_external_id)
const TOAST_OAUTH_FORM_FIELDS = [
  ...OAUTH_FORM_FIELDS,
  {
    label: "Location External ID",
    key: "location_external_id",
    type: "text" as const,
    placeholder: "Enter your Location External ID",
    required: true,
  },
];

// Mindbody-specific form fields
const MINDBODY_FORM_FIELDS = [
  {
    label: "Site ID",
    key: "site_id",
    type: "text" as const,
    placeholder: "Enter your Site ID",
    required: true,
  },
];

export const getFormFields = (
  authType: AuthType,
  serviceName: string,
  providerId?: string,
) => {
  if (isOAuthType(authType)) {
    // Use Toast-specific fields for Toast integration
    const baseFields =
      providerId === "toast" ? TOAST_OAUTH_FORM_FIELDS : OAUTH_FORM_FIELDS;

    return baseFields.map((field) => ({
      ...field,
      placeholder:
        field.key === "api_hostname"
          ? field.placeholder
          : field.placeholder.replace("your", `your ${serviceName}`),
    }));
  }

  if (providerId === "mindbody") {
    return MINDBODY_FORM_FIELDS;
  }

  return []; // API key has no form fields currently
};

// Integration registry with auth type information
export const INTEGRATION_REGISTRY: Record<string, IntegrationProvider> = {
  toast: {
    id: "toast",
    name: "Toast POS",
    navText: "Toast Visits",
    description: "Point of sale and restaurant management system",
    icon: "/static/third_party/toast-slice-icon_color.png",
    iconType: "image",
    category: "POS",
    isPopular: true,
    authType: "oauth_client_credentials",
    testEndpoint: "/config/v2/restaurantServices",
    formFields: getFormFields("oauth_client_credentials", "Toast", "toast"),
  },
  opentable: {
    id: "opentable",
    name: "OpenTable",
    navText: "OpenTable",
    description: "Restaurant reservation and guest management",
    icon: "/static/third_party/opentable_icon.png",
    iconType: "image",
    category: "Reservations",
    isPopular: true,
    authType: "oauth_code",
    testEndpoint: "/v1/me",
    formFields: getFormFields("oauth_code", "OpenTable", "opentable"),
  },
  brivo: {
    id: "brivo",
    name: "Brivo",
    navText: "Brivo",
    description: "Access control and security management",
    icon: Shield,
    iconType: "lucide",
    category: "Security",
    authType: "api_key",
    testEndpoint: "/api/v1/sites",
    formFields: [],
  },
  clubsystems: {
    id: "clubsystems",
    name: "Club Systems",
    navText: "Club Systems",
    description: "Club and membership management platform",
    icon: Users,
    iconType: "lucide",
    category: "Membership",
    authType: "api_key",
    testEndpoint: "/api/v1/members",
    formFields: [],
  },
  mindbody: {
    id: "mindbody",
    name: "Mindbody",
    navText: "Mindbody",
    description: "Fitness and wellness business management",
    icon: "/static/third_party/mindbody-logomark-spearmint.png",
    iconType: "image",
    category: "Fitness",
    isPopular: true,
    authType: "api_key",
    testEndpoint: "/public/v6/site/resources",
    formFields: getFormFields("api_key", "Mindbody", "mindbody"),
  },
  square: {
    id: "square",
    name: "Square",
    navText: "Square",
    description: "Payment processing and business tools",
    icon: Building,
    iconType: "lucide",
    category: "Payments",
    authType: "oauth_code",
    testEndpoint: "/v2/locations",
    formFields: getFormFields("oauth_code", "Square", "square"),
  },
};

export const getIntegrationProvider = (
  id: string,
): IntegrationProvider | undefined => {
  return INTEGRATION_REGISTRY[id];
};

export const getAllIntegrationProviders = (): IntegrationProvider[] => {
  return Object.values(INTEGRATION_REGISTRY);
};

export const getIntegrationProvidersByAuthType = (
  authType: "oauth" | "api_key",
): IntegrationProvider[] => {
  return Object.values(INTEGRATION_REGISTRY).filter(
    (provider) => provider.authType === authType,
  );
};
